import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, HelpCircle, BellRing } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ClubSwitcher from "@/components/ClubSwitcher";
import { NAV, AUTH, ADMIN, MASTER, TEMP_PASSCODE } from "@/constants/testIds";
import { roleLabel } from "@/lib/roleLabels";
import SupportSubmitModal from "@/components/support/SupportSubmitModal";
import api from "@/lib/api";

export default function NavBar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [saBadge, setSaBadge] = useState(null); // { new, breached }

  // Close the mobile drawer on any successful navigation.
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // iter72j · Poll the support badge for SAs so they see new tickets
  // without refreshing the page. Cheap query, once/minute.
  useEffect(() => {
    if (!user || user.role !== "super_admin") return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/super-admin/support/badge");
        if (!cancelled) setSaBadge(data);
      } catch { /* silent */ }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  if (!user) return null;
  const isAdmin = user.role === "club_admin" || user.role === "golf_shop_staff";
  const viaMaster = user.role === "club_admin" && !!user.via_master;
  const viaTemp = user.role === "club_admin" && !!user.via_temp;

  const linkCls = (path) =>
    `text-sm tracking-wider uppercase transition-colors duration-200 ${
      location.pathname === path
        ? "text-[#1A422D] border-b border-[#1A422D] pb-1"
        : "text-[#4A4A4A] hover:text-[#1A422D]"
    }`;

  const handleLogout = async () => {
    const dest = isAdmin ? "/admin/login" : "/login";
    await logout();
    navigate(dest);
  };

  // Single source of truth for which links a given role sees — used by both
  // the desktop nav row and the mobile drawer.
  const navItems = isAdmin
    ? [
        { to: "/admin", label: "Orders", testid: NAV.adminOrders },
        { to: "/admin/members", label: "Members", testid: NAV.adminMembers },
        { to: "/admin/staff", label: "Staff", testid: "nav-admin-staff" },
        { to: "/admin/catalogs", label: "Catalogs", testid: "nav-admin-catalogs" },
        { to: "/admin/reports", label: "Reports", testid: "nav-admin-reports" },
        { to: "/admin/intelligence", label: "Intelligence", testid: ADMIN.navIntelligence },
        { to: "/admin/club-profile", label: "Club Profile", testid: "nav-admin-club-profile" },
        { to: "/profile", label: "Profile", testid: NAV.profile },
      ]
    : [
        { to: "/dashboard", label: "Home", testid: NAV.memberHome },
        { to: "/orders/new", label: "New Order", testid: NAV.memberNew },
        { to: "/orders", label: "History", testid: NAV.memberHistory },
        { to: "/collections", label: "Collections", testid: "nav-collections" },
        { to: "/wishlist", label: "Wishlist", testid: "nav-wishlist" },
        { to: "/crest-profile", label: "Crest Profile", testid: "nav-crest-profile" },
        { to: "/profile", label: "Profile", testid: NAV.profile },
      ];

  return (
    <header className="bg-[#F9F8F4] border-b border-[#E5E3D8] relative">
      {viaMaster && (
        <div
          data-testid={MASTER.pill}
          className="bg-[#B59A5A] text-[#1A1A1A] text-center text-[11px] uppercase tracking-[0.22em] font-semibold py-1.5"
        >
          Signed in via Master Passcode
        </div>
      )}
      {viaTemp && (
        <div
          data-testid={TEMP_PASSCODE.pill}
          className="bg-[#8a1a1a] text-[#F9F8F4] text-center text-[11px] uppercase tracking-[0.22em] font-semibold py-2 px-4 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4"
        >
          <span>Signed in with a Temporary Passcode — set a new permanent one now</span>
          <Link
            to="/admin"
            data-testid={TEMP_PASSCODE.rotateCta}
            className="bg-[#F9F8F4] text-[#8a1a1a] px-3 py-1 hover:bg-[#D9C68A] transition-colors"
          >
            Rotate Passcode
          </Link>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-5 sm:px-10 py-5 sm:py-6 flex items-center justify-between gap-3">
        <Link
          to={isAdmin ? "/admin" : "/dashboard"}
          data-testid={NAV.brand}
          className="flex items-center gap-3 flex-shrink-0"
        >
          <img
            src="/logo.png"
            alt="My Crest Club"
            data-testid="nav-logo"
            className="h-12 sm:h-14 w-auto flex-shrink-0"
          />
          <div className="hidden xl:flex flex-col leading-none">
            <span className="overline whitespace-nowrap">The Pro Shop</span>
            <span className="font-serif text-2xl sm:text-3xl tracking-tight text-[#1A422D] whitespace-nowrap">
              My Crest Club
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {navItems.map((n) => (
            <Link key={n.to} to={n.to} data-testid={n.testid} className={linkCls(n.to)}>
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:block">
            <ClubSwitcher />
          </div>
          {/* iter72j · Support access — visible to every authenticated role. */}
          {user.role === "super_admin" ? (
            <Link
              to="/super-admin/support"
              data-testid="nav-sa-support-badge"
              className="hidden md:inline-flex relative items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[#1A422D] border border-[#1A422D] px-3 py-2 hover:bg-[#1A422D] hover:text-[#F9F8F4]"
              title="Support Inbox"
            >
              <BellRing size={12} />
              Support
              {saBadge?.new > 0 && (
                <span
                  data-testid="nav-sa-support-count"
                  className={`ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] px-1 ${saBadge.breached > 0 ? "bg-[#8a1a1a] text-white" : "bg-[#B59A5A] text-white"}`}
                >
                  {saBadge.new}
                </span>
              )}
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => setShowSupport(true)}
              data-testid="nav-contact-support"
              className="hidden md:inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[#1A422D] hover:text-[#0f1a14]"
              title="Contact Support"
            >
              <HelpCircle size={12} /> Support
            </button>
          )}
          <div className="hidden lg:flex flex-col items-end leading-tight flex-shrink-0">
            <span className="overline whitespace-nowrap" data-testid="nav-role-label">{roleLabel(user)}</span>
            <span className="font-serif text-base text-[#1A1A1A] truncate max-w-[160px]">{user.name}</span>
          </div>
          <button
            type="button"
            data-testid={AUTH.logoutButton}
            onClick={handleLogout}
            className="btn-secondary text-xs hidden md:inline-flex"
          >
            Sign Out
          </button>
          {/* Mobile menu trigger */}
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            data-testid="nav-mobile-trigger"
            aria-label="Open menu"
            className="md:hidden inline-flex items-center justify-center w-10 h-10 border border-[#E5E3D8] text-[#1A422D] hover:bg-[#F3F1E9] transition-colors"
          >
            <Menu size={20} />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {menuOpen && (
        <div
          className="md:hidden fixed inset-0 z-50 flex"
          data-testid="nav-mobile-drawer"
        >
          {/* Backdrop */}
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
            className="flex-1 bg-[#1A1A1A]/70"
          />
          {/* Panel */}
          <aside className="w-72 max-w-[85vw] bg-[#F9F8F4] border-l border-[#E5E3D8] shadow-2xl flex flex-col">
            <div className="px-5 py-5 border-b border-[#E5E3D8] flex items-start justify-between">
              <div>
                <div className="overline" data-testid="nav-mobile-role-label">{roleLabel(user)}</div>
                <div className="font-serif text-xl text-[#1A422D] mt-1 leading-tight">{user.name}</div>
                <div className="text-xs text-[#7A7A7A] mt-1 truncate">{user.email}</div>
              </div>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
                data-testid="nav-mobile-close"
                className="text-[#7A7A7A] hover:text-[#1A422D] -mr-2"
              >
                <X size={22} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-2">
              <div className="px-5 py-3 border-b border-[#E5E3D8]">
                <ClubSwitcher />
              </div>
              {navItems.map((n) => {
                const active = location.pathname === n.to;
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    data-testid={`mobile-${n.testid}`}
                    className={`block px-5 py-4 border-l-4 transition-colors ${
                      active
                        ? "border-[#1A422D] bg-[#E9F0EB] text-[#1A422D]"
                        : "border-transparent text-[#1A1A1A] hover:bg-[#F3F1E9]"
                    }`}
                  >
                    <div className="font-serif text-lg">{n.label}</div>
                  </Link>
                );
              })}
            </nav>
            <div className="px-5 py-5 border-t border-[#E5E3D8]">
              <button
                type="button"
                onClick={handleLogout}
                data-testid="mobile-logout"
                className="btn-secondary text-xs w-full"
              >
                Sign Out
              </button>
            </div>
          </aside>
        </div>
      )}
      <SupportSubmitModal open={showSupport} onClose={() => setShowSupport(false)} />
    </header>
  );
}
